/**
 * React Root Helper Utility
 * Provides compatibility between React 17 (ReactDOM.render) and React 18 (createRoot)
 * 
 * This utility allows the codebase to work with both React 17 and React 18,
 * automatically using the appropriate API based on what's available.
 */

import ReactDOM from "react-dom";

/**
 * Renders a React element into a container
 * Uses createRoot API if available (React 18), otherwise falls back to ReactDOM.render (React 17)
 * 
 * @param {React.ReactElement} element - The React element to render
 * @param {HTMLElement} container - The DOM container to render into
 * @returns {Object|null} - Root instance (React 18) or null (React 17)
 */
export function renderApp(element, container) {
  // Try to use React 18 createRoot API if available
  // Use dynamic require to avoid webpack errors in React 17
  try {
    // eslint-disable-next-line
    const { createRoot } = require("react-dom/client");
    if (createRoot && typeof createRoot === "function") {
      const root = createRoot(container);
      root.render(element);
      return root;
    }
  } catch (e) {
    // react-dom/client not available (React 17), fall back to ReactDOM.render
  }
  
  // React 17 fallback
  ReactDOM.render(element, container);
  return null;
}

/**
 * Unmounts a React tree from a container
 * Uses root.unmount() if root instance provided (React 18), otherwise uses ReactDOM.unmountComponentAtNode (React 17)
 * 
 * @param {HTMLElement} container - The DOM container to unmount from
 * @param {Object|null} rootInstance - Root instance from renderApp (React 18) or null (React 17)
 */
export function unmountApp(container, rootInstance = null) {
  if (rootInstance && typeof rootInstance.unmount === "function") {
    // React 18: use root.unmount()
    rootInstance.unmount();
  } else {
    // React 17: use ReactDOM.unmountComponentAtNode
    ReactDOM.unmountComponentAtNode(container);
  }
}

